import { useState, useEffect } from 'react'
import * as safe from '../../lib/safeStorage'
import { Api } from '../../lib/apiClient'

type Partner = {
  _id: string
  name: string
  email?: string
  phone?: string
  preferences?: any
  notes?: string
}

type Plan = {
  _id: string
  title: string
  dateTypes: string[]
  places: any[]
  notes: string
  partnerId?: string
  partnerName?: string
  isDefault: boolean
  createdAt: string
}

export default function CreatePlanPanel() {
  const [title, setTitle] = useState('')
  const [dateTypes, setDateTypes] = useState<string[]>(() => {
    try { return JSON.parse(safe.getItem('ds_prefs') || '{}')?.dateTypes || [] } catch { return [] }
  })
  const [selectedPartner, setSelectedPartner] = useState<string>('')
  const [partners, setPartners] = useState<Partner[]>([])
  const [savedPlans, setSavedPlans] = useState<Plan[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [justSaved, setJustSaved] = useState(false)
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null)
  const [deletingPlan, setDeletingPlan] = useState<string | null>(null)

  const toggle = (t: string) => setDateTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])

  // Load partners and plans on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Check if user is authenticated (has token)
        const token = safe.getItem('ds_token')
        if (!token) {
          // For guest users, set empty data and return
          setPartners([])
          setSavedPlans([])
          return
        }
        
        const [partnersData, plansData] = await Promise.all([
          Api.partners.list(),
          Api.plans.list()
        ])
        
        setPartners(partnersData.partners || [])
        setSavedPlans(plansData.plans || [])
      } catch (e) {
        console.log('Failed to load data:', e)
        // Set empty data on error
        setPartners([])
        setSavedPlans([])
      }
    }
    
    loadData()
  }, [])

  const toggleDefaultPlan = async (planId: string) => {
    try {
      await Api.plans.setDefault(planId)
      // Refresh saved plans
      const plansData = await Api.plans.list()
      setSavedPlans(plansData.plans || [])
    } catch (e) {
      console.log('Failed to toggle default plan:', e)
    }
  }

  const startEdit = (plan: Plan) => {
    setEditingPlan(plan)
    setTitle(plan.title)
    setDateTypes(plan.dateTypes)
    setSelectedPartner(plan.partnerId || '')
  }

  const cancelEdit = () => {
    setEditingPlan(null)
    setTitle('')
    setDateTypes([])
    setSelectedPartner('')
    setError(null)
  }

  const updatePlan = async () => {
    if (!editingPlan || !title.trim()) { setError('Title is required'); return }
    
    // Check if partner is required for non-Solo date types
    const needsPartner = dateTypes.some(type => type !== 'Solo')
    if (needsPartner && !selectedPartner) {
      setError('Please select a partner for this date type')
      return
    }
    
    setSaving(true); setError(null)
    try {
      const selectedPartnerData = partners.find(p => p._id === selectedPartner)
      await Api.plans.update(editingPlan._id, { 
        title, 
        dateTypes, 
        partnerId: selectedPartner || null,
        partnerName: selectedPartnerData?.name || ''
      })
      
      // Refresh saved plans
      const plansData = await Api.plans.list()
      setSavedPlans(plansData.plans || [])
      
      setJustSaved(true); cancelEdit()
      setTimeout(() => setJustSaved(false), 1200)
    } catch (e: any) { setError(e.message || 'Failed to update') }
    finally { setSaving(false) }
  }

  const deletePlan = async (planId: string) => {
    if (!confirm('Are you sure you want to delete this plan?')) return
    
    setDeletingPlan(planId)
    try {
      await Api.plans.delete(planId)
      // Refresh saved plans
      const plansData = await Api.plans.list()
      setSavedPlans(plansData.plans || [])
    } catch (e) {
      console.log('Failed to delete plan:', e)
    } finally {
      setDeletingPlan(null)
    }
  }

  const save = async () => {
    if (editingPlan) {
      await updatePlan()
      return
    }
    
    if (!title.trim()) { setError('Title is required'); return }
    
    // Check if partner is required for non-Solo date types
    const needsPartner = dateTypes.some(type => type !== 'Solo')
    if (needsPartner && !selectedPartner) {
      setError('Please select a partner for this date type')
      return
    }
    
    setSaving(true); setError(null)
    try {
      const selectedPartnerData = partners.find(p => p._id === selectedPartner)
      await Api.plans.create({ 
        title, 
        dateTypes, 
        partnerId: selectedPartner || null,
        partnerName: selectedPartnerData?.name || ''
      })
      
      // Refresh saved plans
      const plansData = await Api.plans.list()
      setSavedPlans(plansData.plans || [])
      
      setJustSaved(true); setTitle(''); setSelectedPartner('')
      setTimeout(() => setJustSaved(false), 1200)
    } catch (e: any) { setError(e.message || 'Failed to save') }
    finally { setSaving(false) }
  }

  return (
    <div id="create-plan" className="glass-card p-4">
      <div className="flex items-center justify-between relative">
        <h2 className="text-xl font-semibold text-black">
          {editingPlan ? 'Edit Date Plan' : 'Create Date Plan'}
        </h2>
        {justSaved && <span className="absolute right-0 -top-6 text-xs bg-white/90 border border-black/10 rounded-full px-3 py-1 shadow">Saved</span>}
      </div>
      {error && <div className="mt-2 text-red-600 text-sm">{error}</div>}
      <div className="mt-3 flex flex-col gap-3">
        <input className="w-full border p-3 rounded-xl" placeholder="Plan title" value={title} onChange={e => setTitle(e.target.value)} />
        
        <div className="text-sm font-semibold text-black/90">Date Types</div>
        <div className="flex flex-wrap gap-2">
          {['Solo','Romantic','First Date','Casual','Fun','Active','Lunch','Dinner','Night Out'].map(l => (
            <button key={l} className={`chip ${dateTypes.includes(l) ? 'chip-selected' : ''}`} onClick={() => toggle(l)}>{l}</button>
          ))}
        </div>

        {/* Partner Selection - only show if non-Solo date types are selected */}
        {dateTypes.some(type => type !== 'Solo') && (
          <div>
            <div className="text-sm font-semibold text-black/90 mb-2">Select Partner</div>
            <select 
              value={selectedPartner} 
              onChange={(e) => setSelectedPartner(e.target.value)}
              className="w-full border p-3 rounded-xl"
            >
              <option value="">Choose a partner...</option>
              {(partners || []).map(partner => (
                <option key={partner._id} value={partner._id}>{partner.name}</option>
              ))}
            </select>
            {partners.length === 0 && (
              <div className="text-sm text-black/70 mt-1">
                No partners yet. <button 
                  onClick={() => {
                    const el = document.getElementById('partners-section')
                    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                  className="underline hover:text-black/90"
                >
                  Add a partner
                </button> to create shared plans.
              </div>
            )}
          </div>
        )}

        <div className="mt-3 flex gap-3">
          <button className="btn-primary px-6 py-3" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : (editingPlan ? 'Update Plan' : 'Save Plan')}
          </button>
          {editingPlan && (
            <button className="px-6 py-3 border border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50" onClick={cancelEdit}>
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Saved Plans Section */}
      {savedPlans.length > 0 && (
        <div className="mt-6 pt-4 border-t border-black/10">
          <h3 className="text-lg font-semibold text-black mb-3">Saved Plans</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-black/10">
                  <th className="text-left py-2 font-semibold text-black/90">Title</th>
                  <th className="text-left py-2 font-semibold text-black/90">Date Types</th>
                  <th className="text-left py-2 font-semibold text-black/90">Partner</th>
                  <th className="text-left py-2 font-semibold text-black/90">Date Updated</th>
                  <th className="text-left py-2 font-semibold text-black/90">Default</th>
                  <th className="text-left py-2 font-semibold text-black/90">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(savedPlans || []).map(plan => (
                  <tr key={plan._id} className="border-b border-black/5 hover:bg-white/30">
                    <td className="py-3 font-medium text-black">{plan.title}</td>
                    <td className="py-3 text-black/70">{plan.dateTypes.join(', ')}</td>
                    <td className="py-3 text-black/70">{plan.partnerName || 'Solo'}</td>
                    <td className="py-3 text-black/50">{new Date(plan.createdAt).toLocaleDateString()}</td>
                    <td className="py-3">
                      <button
                        onClick={() => toggleDefaultPlan(plan._id)}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          plan.isDefault 
                            ? 'bg-orange-500' 
                            : 'bg-gray-300'
                        }`}
                        title={plan.isDefault ? 'Click to turn off default' : 'Click to set as default'}
                      >
                        <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          plan.isDefault ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </td>
                    <td className="py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEdit(plan)}
                          className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                          disabled={editingPlan !== null}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deletePlan(plan._id)}
                          className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                          disabled={deletingPlan === plan._id}
                        >
                          {deletingPlan === plan._id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}


